import { useRef, useState } from "react";
import { runAiSearch } from "@/api/discovery";
import { parseLeadEnvelopes } from "@/lib/lead-envelope";

export function CompanyResearchAiModal({
  open,
  onClose,
  onApplyKeywords,
  onSaveAsDeals,
  icpContext,
}: {
  open: boolean;
  onClose: () => void;
  onApplyKeywords: (keywords: string[]) => void;
  onSaveAsDeals: (text: string) => void;
  icpContext?: string;
}) {
  const [prompt, setPrompt] = useState("");
  const [streamText, setStreamText] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  if (!open) return null;

  async function run() {
    const q = prompt.trim();
    if (!q) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setError(null);
    setStreamText("");
    const fullQuery = icpContext
      ? `${q}\n\nICP context:\n${icpContext}`
      : q;
    try {
      await runAiSearch(
        fullQuery,
        (event) => {
          if (event.type === "text") {
            setStreamText((prev) => prev + event.text);
          } else if (event.type === "error") {
            setError(event.msg);
          }
        },
        ac.signal,
      );
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setError(err instanceof Error ? err.message : "Research failed");
      }
    } finally {
      setRunning(false);
    }
  }

  function keywordsFromStream(): string[] {
    const leads = parseLeadEnvelopes(streamText);
    if (leads.length) {
      return [
        ...new Set(
          leads
            .flatMap((l) => [l.company, l.title, l.dealName].filter(Boolean))
            .map((s) => String(s)),
        ),
      ];
    }
    return streamText
      .split(/[\s,]+/)
      .map((w) => w.trim())
      .filter((w) => w.length > 3)
      .slice(0, 12);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg border border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold">Research with AI</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Describe target accounts. Results can seed company keyword tags or
            save as deals.
          </p>
        </div>
        <div className="space-y-3 overflow-y-auto px-4 py-3">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder="e.g. Series B fintechs in the US hiring revenue ops"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <button
            type="button"
            disabled={running || !prompt.trim()}
            onClick={() => void run()}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
          >
            {running ? "Researching…" : "Run research"}
          </button>
          {error && <p className="text-xs text-destructive">{error}</p>}
          {streamText && (
            <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted/10 p-3 text-xs">
              {streamText}
            </pre>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
          <button
            type="button"
            disabled={!streamText}
            onClick={() => {
              onApplyKeywords(keywordsFromStream());
              onClose();
            }}
            className="rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-40"
          >
            Apply keywords to filters
          </button>
          <button
            type="button"
            disabled={!streamText}
            onClick={() => {
              onSaveAsDeals(streamText);
              onClose();
            }}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-40"
          >
            Save as deals
          </button>
        </div>
      </div>
    </div>
  );
}
